const APP_LOCK_ENABLED_KEY = "WR_APP_LOCK_ENABLED";
const APP_LOCK_SKIP_UNTIL_KEY = "WR_APP_LOCK_SKIP_UNTIL";

export function isCapacitorRuntime() {
    try {
        return typeof window !== "undefined" && !!window.Capacitor;
    } catch (_) {
        return false;
    }
}

function getCapacitor() {
    try {
        return window?.Capacitor || null;
    } catch (_) {
        return null;
    }
}

function getCapPlugins() {
    return getCapacitor()?.Plugins || null;
}

function getBiometricLockPlugin() {
    const Cap = getCapacitor();
    if (!Cap) return null;

    const existing = Cap.Plugins?.BiometricLock;
    if (existing?.isAvailable && existing?.verify) return existing;

    try {
        if (typeof Cap.registerPlugin === "function") {
            return Cap.registerPlugin("BiometricLock");
        }
    } catch (_) {
        // ignore
    }
    return existing || null;
}

async function prefGet(key) {
    const plugins = getCapPlugins();
    const Preferences = plugins?.Preferences;
    if (isCapacitorRuntime() && Preferences?.get) {
        const res = await Preferences.get({ key });
        return res?.value ?? null;
    }
    return localStorage.getItem(key);
}

async function prefSet(key, value) {
    const plugins = getCapPlugins();
    const Preferences = plugins?.Preferences;
    if (isCapacitorRuntime() && Preferences?.set) {
        await Preferences.set({ key, value });
        return;
    }
    localStorage.setItem(key, value);
}

export function markAppLockSkipPrompt(ms = 8000) {
    const until = String(Date.now() + Math.max(0, Number(ms) || 0));
    try {
        sessionStorage.setItem(APP_LOCK_SKIP_UNTIL_KEY, until);
    } catch (_) {
        // ignore
    }
    try {
        localStorage.setItem(APP_LOCK_SKIP_UNTIL_KEY, until);
    } catch (_) {
        // ignore
    }
}

export function shouldSkipAppLockPrompt() {
    try {
        const until = Number(
            sessionStorage.getItem(APP_LOCK_SKIP_UNTIL_KEY)
            || localStorage.getItem(APP_LOCK_SKIP_UNTIL_KEY)
            || 0,
        );
        if (Number.isFinite(until) && until > Date.now()) {
            return true;
        }
    } catch (_) {
        // ignore
    }
    return false;
}

export async function getAppLockEnabled() {
    const value = await prefGet(APP_LOCK_ENABLED_KEY);
    return value === "1";
}

export async function setAppLockEnabled(enabled) {
    await prefSet(APP_LOCK_ENABLED_KEY, enabled ? "1" : "0");
    if (enabled) {
        markAppLockSkipPrompt(8000);
    }
    try {
        window.dispatchEvent(new Event("wr-app-lock-changed"));
    } catch (_) {
        // ignore
    }
}

export async function isBiometricLockAvailable() {
    if (!isCapacitorRuntime()) return false;

    const BiometricLock = getBiometricLockPlugin();
    if (!BiometricLock?.isAvailable) return false;

    try {
        const res = await BiometricLock.isAvailable();
        return !!res?.isAvailable;
    } catch (_) {
        return false;
    }
}

/** @returns {Promise<{ isAvailable: boolean, code: number|null }>} */
export async function getBiometricLockStatus() {
    if (!isCapacitorRuntime()) return { isAvailable: false, code: null };

    const BiometricLock = getBiometricLockPlugin();
    if (!BiometricLock?.isAvailable) return { isAvailable: false, code: null };

    try {
        const res = await BiometricLock.isAvailable();
        return {
            isAvailable: !!res?.isAvailable,
            code: Number.isFinite(Number(res?.code)) ? Number(res.code) : null,
        };
    } catch (_) {
        return { isAvailable: false, code: null };
    }
}

export function formatBiometricUnavailableMessage(code) {
    // BiometricManager.BIOMETRIC_ERROR_NONE_ENROLLED = 11
    if (Number(code) === 11) {
        return "❌ Спочатку встановіть ПІН/пароль або біометрію в налаштуваннях Android";
    }
    return "❌ Біометрія/ПІН недоступні на цьому пристрої";
}

let verifyInFlight = null;

export async function verifyBiometricLock(options = {}) {
    if (!isCapacitorRuntime()) throw new Error("Not running in Capacitor");

    if (verifyInFlight) {
        return verifyInFlight;
    }

    const BiometricLock = getBiometricLockPlugin();
    if (!BiometricLock?.verify) throw new Error("BiometricLock plugin is not available");

    verifyInFlight = (async () => {
        try {
            await BiometricLock.verify({
                title: options.title || "Розблокування",
                subtitle: options.subtitle || undefined,
                description: options.description || undefined,
            });
        } finally {
            verifyInFlight = null;
        }
    })();

    return verifyInFlight;
}
